import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Calendar, MessageCircle, Download, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { categories, Category, PriceItem } from '../data/priceData';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  category,
  isOpen,
  onToggle,
  lang,
  isHighlighted
}: {
  category: Category;
  isOpen: boolean;
  onToggle: () => void;
  lang: 'en' | 'ua' | 'ru' | 'ro';
  isHighlighted?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      const isMobile = window.innerWidth < 1024;
      if (isMobile && headerRef.current) {
        setTimeout(() => {
          const el = headerRef.current;
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    } else {
      setHeight(0);
    }
  }, [isOpen]);
  return <div id={category.anchorId} className={`border-b border-beige/60 last:border-b-0 transition-all duration-500 rounded-lg ${isHighlighted ? 'ring-2 ring-forest-500 bg-ivory-200/50' : ''}`} data-id="element-2956">
      <button ref={headerRef} onClick={onToggle} className="w-full flex items-center justify-between min-h-[56px] py-4 px-2 text-left cursor-pointer select-none" aria-expanded={isOpen} data-id="element-2957">
        <span className={`text-[15px] font-semibold transition-colors duration-200 ${isOpen ? 'text-forest-600' : 'text-charcoal'}`} data-id="element-2958">
          {category.title[lang]}
        </span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-250 ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-200 text-stone-500'}`} data-id="element-2959">
          {isOpen ? <Minus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2960" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2961" />}
        </span>
      </button>

      <div style={{
      maxHeight: isOpen ? `${height}px` : '0px'
    }} className="overflow-hidden transition-[max-height] duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]" data-id="element-2962">
        <div ref={contentRef} className={`pb-6 px-2 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`} data-id="element-2963">
          <div className="space-y-3.5" data-id="element-2964">
            {category.items.map((item, idx) => <div key={idx} className="flex items-baseline gap-2" data-id="element-2965">
                <span className="text-[13px] text-charcoal-light leading-snug" data-id="element-2966">
                  {item.name[lang]}
                </span>
                <span className="flex-grow border-b border-dotted border-beige min-w-[16px] relative top-[-3px]" data-id="element-2967" />
                <span className="text-[13px] font-bold text-forest-700 whitespace-nowrap tabular-nums" data-id="element-2968">
                  {item.price}
                </span>
              </div>)}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-2969">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2970">
              <Button size="sm" className="w-full gap-2" data-id="element-2971">
                <Calendar className="w-4 h-4" data-id="element-2972" />
                {lang === 'en' ? 'Book Online' : lang === 'ua' ? 'Записатись онлайн' : lang === 'ro' ? 'Programare Online' : 'Записаться онлайн'}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2973">
              <Button variant="whatsapp" size="sm" className="w-full gap-2" data-id="element-2974">
                <MessageCircle className="w-4 h-4" data-id="element-2975" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>;
}
export function PricesPage() {
  const {
    t,
    lang
  } = useTranslation();
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>('consultations');
  const seoTitle = lang === 'en' ? 'Dental Prices in Angel, London | Dentico Clinic' : lang === 'ua' ? 'Ціни на стоматологію в Angel, Лондон | Dentico Clinic' : lang === 'ro' ? 'Prețuri Dentare în Angel, Londra | Dentico Clinic' : 'Цены на стоматологию в Angel, Лондон | Dentico Clinic';
  const seoDescription = lang === 'en' ? 'View transparent private dental prices at Dentico Clinic in Angel, London. Fees for hygiene, fillings, crowns, implants, orthodontics and more.' : lang === 'ua' ? 'Перегляньте прозорі ціни на приватну стоматологію в Dentico Clinic в Angel, Лондон. Вартість гігієни, пломб, коронок, імплантів, ортодонтії та іншого.' : lang === 'ro' ? 'Vizualizați prețurile transparente pentru stomatologie privată la Dentico Clinic din Angel, Londra. Tarife pentru igienă, plombe, coroane, implanturi, ortodonție și multe altele.' : 'Посмотрите прозрачные цены на частную стоматологию в Dentico Clinic в Angel, Лондон. Стоимость гигиены, пломб, коронок, имплантов, ортодонтии и многого другого.';
  const [highlightedAnchor, setHighlightedAnchor] = useState<string | null>(null);
  const hasScrolledRef = useRef(false);
  const toggle = useCallback((id: string) => {
    setOpenId(prev => prev === id ? null : id);
  }, []);
  // Robust hash-based scroll — retries until element is found (mobile timing fix)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) {
      hasScrolledRef.current = false;
      return;
    }
    const matchedCategory = categories.find(cat => cat.anchorId === hash);
    if (!matchedCategory) return;
    // Open the accordion and highlight
    setOpenId(matchedCategory.id);
    setHighlightedAnchor(matchedCategory.anchorId);
    // Scroll to element with retry logic for mobile
    const HEADER_OFFSET = 100; // sticky header height + padding
    let attempts = 0;
    const maxAttempts = 20; // retry for ~2 seconds
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        hasScrolledRef.current = true;
        return true;
      }
      return false;
    };
    // Try immediately
    if (tryScroll()) {
      // Also re-scroll after a beat for layout shifts
      setTimeout(tryScroll, 300);
    } else {
      // Retry with interval
      const interval = setInterval(() => {
        attempts++;
        if (tryScroll() || attempts >= maxAttempts) {
          clearInterval(interval);
          // One more attempt after accordion animation
          if (attempts < maxAttempts) {
            setTimeout(tryScroll, 350);
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
    // Remove highlight after 2.5s
    const highlightTimer = setTimeout(() => {
      setHighlightedAnchor(null);
    }, 2500);
    return () => clearTimeout(highlightTimer);
  }, [location.hash, location.key]);
  const midpoint = Math.ceil(categories.length / 2);
  const leftColumn = categories.slice(0, midpoint);
  const rightColumn = categories.slice(midpoint);
  const aligner = {
    title: {
      en: 'Clear Aligner Treatment',
      ua: 'Лікування прозорими елайнерами',
      ru: 'Лечение прозрачными элайнерами',
      ro: 'Tratament cu Aligneri Transparenți'
    },
    subtitle: {
      en: 'Discreet teeth straightening with clear removable aligners, planned individually by our orthodontic team.',
      ua: 'Непомітне вирівнювання зубів за допомогою прозорих знімних елайнерів, що плануються індивідуально нашою командою ортодонтів.',
      ru: 'Незаметное выравнивание зубов с помощью прозрачных съёмных элайнеров, разрабатываемых индивидуально нашей командой ортодонтов.',
      ro: 'Îndreptarea discretă a dinților cu aligneri transparenți detașabili, planificați individual de echipa noastră de ortodonți.'
    },
    mostPopular: {
      en: 'Most Common',
      ua: 'Найпопулярніше',
      ru: 'Самый популярный',
      ro: 'Cel mai ales'
    },
    plans: [{
      name: {
        en: 'Mild Case',
        ua: 'Легкий випадок',
        ru: 'Лёгкий случай',
        ro: 'Caz Ușor'
      },
      price: 'from £2,995',
      desc: {
        en: 'Suitable for minor tooth movement and simple alignment cases.',
        ua: 'Підходить для незначного переміщення зубів та простого вирівнювання.',
        ru: 'Подходит для небольшого перемещения зубов и простого выравнивания.',
        ro: 'Potrivit pentru mișcări dentare minore și cazuri simple de aliniere.'
      }
    }, {
      name: {
        en: 'Moderate Case',
        ua: 'Помірний випадок',
        ru: 'Умеренный случай',
        ro: 'Caz Moderat'
      },
      price: 'from £3,995',
      desc: {
        en: 'Suitable for more comprehensive upper and lower arch alignment.',
        ua: 'Підходить для більш комплексного вирівнювання верхньої та нижньої щелеп.',
        ru: 'Подходит для более комплексного выравнивания верхней и нижней челюстей.',
        ro: 'Potrivit pentru o aliniere mai amplă a arcadelor superioară și inferioară.'
      }
    }, {
      name: {
        en: 'Complex Case',
        ua: 'Складний випадок',
        ru: 'Сложный случай',
        ro: 'Caz Complex'
      },
      price: 'from £4,995',
      desc: {
        en: 'Suitable for more advanced cases requiring a more detailed treatment plan.',
        ua: 'Підходить для складніших випадків, що потребують детальнішого плану лікування.',
        ru: 'Подходит для более сложных случаев, требующих детального плана лечения.',
        ro: 'Potrivit pentru cazuri mai avansate care necesită un plan de tratament detaliat.'
      }
    }],
    note: {
      en: 'The final treatment cost depends on the complexity of your case and will be confirmed after your consultation, 3D scan and orthodontic assessment.',
      ua: 'Остаточна вартість лікування залежить від складності вашого випадку та буде підтверджена після консультації, 3D-сканування та ортодонтичного огляду.',
      ru: 'Итоговая стоимость лечения зависит от сложности вашего случая и будет подтверждена после консультации, 3D-сканирования и ортодонтической оценки.',
      ro: 'Costul final al tratamentului depinde de complexitatea cazului dvs. și va fi confirmat după consultație, scanarea 3D și evaluarea ortodontică.'
    },
    cta: {
      en: 'Book Clear Aligner Consultation',
      ua: 'Записатися на консультацію щодо елайнерів',
      ru: 'Записаться на консультацию по элайнерам',
      ro: 'Programare Consultație Aligneri'
    },
    smallText: {
      en: 'Payment options may be available. Retainers may be required after treatment and will be discussed during your consultation.',
      ua: 'Можливі варіанти оплати частинами. Після лікування можуть знадобитися ретейнери, що обговорюється під час консультації.',
      ru: 'Возможны варианты оплаты в рассрочку. После лечения могут потребоваться ретейнеры, что обсуждается на консультации.',
      ro: 'Pot fi disponibile opțiuni de plată. După tratament pot fi necesari retaineri, aspect discutat în cadrul consultației.'
    }
  };
  const alignerLang = lang as 'en' | 'ua' | 'ru' | 'ro';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-2976">
      {/* Hero */}
      <section className="bg-forest-900 py-16 lg:py-20 text-white" data-id="element-2977">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2978">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-2979">
            {lang === 'en' ? 'Dental Prices in Angel, London' : lang === 'ua' ? 'Ціни на стоматологію в Angel, Лондон' : lang === 'ro' ? 'Prețuri Dentare în Angel, Londra' : 'Цены на стоматологию в Angel, Лондон'}
          </h1>
          <p className="text-lg text-ivory/80 max-w-2xl mx-auto" data-id="element-2980">
            {lang === 'en' ? 'We believe patients should feel informed before starting treatment. Our private dental fees are shown clearly below, and your dentist will confirm the final cost after consultation and assessment.' : lang === 'ua' ? 'Ми віримо, що пацієнти повинні бути поінформовані перед початком лікування. Наші ціни на приватну стоматологію чітко вказані нижче, а ваш стоматолог підтвердить остаточну вартість після консультації та огляду.' : lang === 'ro' ? 'Credem că pacienții ar trebui să se simtă informați înainte de a începe tratamentul. Tarifele noastre dentare private sunt afișate clar mai jos, iar medicul dentist va confirma costul final după consultație și evaluare.' : 'Мы верим, что пациенты должны быть проинформированы перед началом лечения. Наши цены на частную стоматологию четко указаны ниже, а ваш стоматолог подтвердит окончательную стоимость после консультации и осмотра.'}
          </p>
        </div>
      </section>

      {/* Accordion Price List */}
      <section className="py-12 lg:py-20 bg-ivory-200" data-id="element-2981">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2982">
          <div className="grid lg:grid-cols-2 gap-x-10 gap-y-0" data-id="element-2983">
            <div className="bg-ivory rounded-2xl shadow-sm border border-beige/60 px-6 sm:px-8 py-2 mb-4 lg:mb-0" data-id="element-2984">
              {leftColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2985" />)}
            </div>
            <div className="bg-ivory rounded-2xl shadow-sm border border-beige/60 px-6 sm:px-8 py-2" data-id="element-2986">
              {rightColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2987" />)}
            </div>
          </div>

          {/* Note */}
          <p className="mt-10 text-center text-stone-500 text-sm max-w-2xl mx-auto" data-id="element-2988">
            {lang === 'en' ? 'Prices may vary depending on clinical complexity. A consultation is required before confirming a final treatment plan.' : lang === 'ua' ? 'Ціни можуть змінюватися залежно від клінічної складності. Перед підтвердженням остаточного плану лікування необхідна консультація.' : lang === 'ro' ? 'Prețurile pot varia în funcție de complexitatea clinică. Este necesară o consultație înainte de a confirma un plan de tratament final.' : 'Цены могут варьироваться в зависимости от клинической сложности. Перед подтверждением окончательного плана лечения необходима консультация.'}
          </p>

          {/* Footer note */}
          <p className="mt-2 text-center text-stone-500 text-xs max-w-2xl mx-auto" data-id="element-2989">
            {lang === 'en' ? 'Prices may vary. Consultation required.' : lang === 'ua' ? 'Ціни можуть відрізнятися. Необхідна консультація.' : lang === 'ro' ? 'Prețurile pot varia. Este necesară o consultație.' : 'Цены могут отличаться. Необходима консультация.'}
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3" data-id="element-2990">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2991">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2992">
                <Calendar className="w-5 h-5" data-id="element-2993" />
                {t('prices.bookCta')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2994">
              <Button variant="whatsapp" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2995">
                <MessageCircle className="w-5 h-5" data-id="element-2996" />
                WhatsApp
              </Button>
            </a>
            <Link to={`/${lang}/price-list`} target="_blank" rel="noopener noreferrer" data-id="element-2997">
              <Button variant="secondary" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2998">
                <Download className="w-5 h-5" data-id="element-2999" />
                {lang === 'en' ? 'Print Price List' : lang === 'ua' ? 'Роздрукувати прайс' : lang === 'ro' ? 'Imprimare Prețuri' : 'Распечатать прайс'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Clear Aligner Treatment */}
      <section className="py-16 lg:py-24 bg-ivory" data-id="element-3000">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3001">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-3002">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-3003">
              {aligner.title[alignerLang]}
            </h2>
            <p className="text-base md:text-lg text-charcoal-light leading-relaxed" data-id="element-3004">
              {aligner.subtitle[alignerLang]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7 items-stretch" data-id="element-3005">
            {aligner.plans.map((plan, idx) => {
            const isFeatured = idx === 1;
            return <div key={idx} className={`relative flex flex-col rounded-2xl bg-ivory p-8 transition-all duration-300 ${isFeatured ? 'border-2 border-forest-600 shadow-xl md:-translate-y-2' : 'border border-beige/60 shadow-sm hover:shadow-md'}`} data-id="element-3006">
                  {isFeatured && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-forest-600 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm" data-id="element-3007">
                      {aligner.mostPopular[alignerLang]}
                    </span>}
                  <h3 className={`text-xl font-bold ${isFeatured ? 'text-forest-700' : 'text-charcoal'}`} data-id="element-3008">
                    {plan.name[alignerLang]}
                  </h3>
                  <p className="mt-4 mb-1 text-4xl font-bold text-charcoal tabular-nums" data-id="element-3009">
                    {plan.price}
                  </p>
                  <div className={`my-5 h-px w-full ${isFeatured ? 'bg-forest-100' : 'bg-beige/60'}`} data-id="element-3010" />
                  <p className="text-sm leading-relaxed text-charcoal-light flex-grow" data-id="element-3011">
                    {plan.desc[alignerLang]}
                  </p>
                </div>;
          })}
          </div>

          {/* Note */}
          <p className="mt-10 text-center text-sm text-stone-500 max-w-3xl mx-auto leading-relaxed" data-id="element-3012">
            {aligner.note[alignerLang]}
          </p>

          {/* CTA */}
          <div className="mt-8 flex justify-center" data-id="element-3013">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3014">
              <Button size="lg" className="gap-2" data-id="element-3015">
                <Calendar className="w-5 h-5" data-id="element-3016" />
                {aligner.cta[alignerLang]}
              </Button>
            </a>
          </div>

          {/* Optional small text */}
          <p className="mt-6 text-center text-xs text-stone-500 max-w-2xl mx-auto leading-relaxed" data-id="element-3017">
            {aligner.smallText[alignerLang]}
          </p>
        </div>
      </section>

      {/* Services Link */}
      <section className="py-16 bg-ivory-200" data-id="element-3018">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3019">
          <h2 className="text-2xl font-bold text-charcoal mb-4" data-id="element-3020">
            {t('services.title')}
          </h2>
          <p className="text-charcoal-light mb-8" data-id="element-3021">{t('services.subtitle')}</p>
          <Link to={`/${lang}/services`} data-id="element-3022">
            <Button variant="secondary" size="lg" data-id="element-3023">
              {t('services.viewAll')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}